import { normalizeNullableId } from '@/lib/services/occasions/occasions.utils'
import { idSchema } from '@/lib/utils'
import dayjs from '@/lib/dayjs'
import { z } from 'zod/v4'
import type {
  PriceAdjustmentEndType,
  PriceAdjustmentOperation,
  PriceAdjustmentRoundingMode,
  PriceAdjustmentScopeType,
  PriceAdjustmentStartType,
  PriceAdjustmentType,
  PriceAdjustmentWhenType,
  ProductPriceAdjustment,
} from './priceAdjustments.types'

export const priceAdjustmentSchema = z.object({
  priceAdjustmentId: idSchema('price change'),
})

export const priceAdjustmentSearchSchema = z.object({
  collectionId: idSchema('collection', true),
})

export const PRICE_ADJUSTMENT_SCOPE_GLOBAL = 'global'
export const PRICE_ADJUSTMENT_SCOPE_COLLECTIONS = 'collections'

export const PRICE_ADJUSTMENT_ROUNDING_NONE = 'none'
export const PRICE_ADJUSTMENT_ROUNDING_NEAREST_HALF_DOLLAR = 'nearest_half_dollar'
export const PRICE_ADJUSTMENT_ROUNDING_NEAREST_DOLLAR = 'nearest_dollar'

export const PRICE_ADJUSTMENT_PRIORITY_NORMAL = 0
export const PRICE_ADJUSTMENT_PRIORITY_HIGH = 100

export const PRICE_ADJUSTMENT_WHEN_TYPE_ACTIVE_UNTIL_DELETED = 'active_until_deleted'
export const PRICE_ADJUSTMENT_WHEN_TYPE_SCHEDULED = 'scheduled'
export const PRICE_ADJUSTMENT_WHEN_TYPE_PERMANENT = 'permanent'

export const PRICE_ADJUSTMENT_DISABLE_MODE_UNTIL_REENABLED = 'until_reenabled'
export const PRICE_ADJUSTMENT_DISABLE_MODE_THIS_OCCURRENCE = 'this_occurrence'

export const PRICE_ADJUSTMENT_START_TYPE_FIXED_DATE = 'fixed_date'
export const PRICE_ADJUSTMENT_START_TYPE_HOLIDAY_OFFSET = 'holiday_offset'
export const PRICE_ADJUSTMENT_START_TYPE_PERMANENT = 'permanent'

export const PRICE_ADJUSTMENT_END_TYPE_NONE = 'none'
export const PRICE_ADJUSTMENT_END_TYPE_FIXED_DATE = 'fixed_date'
export const PRICE_ADJUSTMENT_END_TYPE_HOLIDAY_OFFSET = 'holiday_offset'
export const PRICE_ADJUSTMENT_END_TYPE_DAYS_AFTER_START = 'days_after_start'

/** A form field the florist types a number into: blank, text, or a number. */
type NumberishInput = string | number | null | undefined

/** A schedule field holding a number as the form edits it. */
type PriceAdjustmentNumberishFormValue = string | number | null

/** The price change form's schedule fields. */
export interface PriceAdjustmentFormSchedule {
  is_enabled: boolean
  /** Read-only: when a skipped yearly occurrence resumes. */
  suppressed_until_on: string | null
  start_type: PriceAdjustmentStartType | ''
  starts_on: string
  start_holiday: string
  start_occasion_id: number | string | null
  start_offset_days: PriceAdjustmentNumberishFormValue
  end_type: PriceAdjustmentEndType | ''
  ends_on: string
  end_holiday: string
  end_occasion_id: number | string | null
  end_offset_days: PriceAdjustmentNumberishFormValue
  end_days_after_start: PriceAdjustmentNumberishFormValue
  is_yearly: boolean
  end_matches_start_holiday: boolean
}

/** The price change form's values; `priceAdjustmentForm` validates them. */
export interface PriceAdjustmentFormValues {
  name: string
  scope_type: PriceAdjustmentScopeType | ''
  when_type: PriceAdjustmentWhenType | ''
  /** Set for saved price changes. */
  can_still_affect_future_pricing?: boolean
  collection_ids: (number | string)[]
  rounding_mode: PriceAdjustmentRoundingMode | ''
  adjustment_type: PriceAdjustmentType | ''
  operation: PriceAdjustmentOperation | ''
  amount: string | number
  priority: string | number
  schedule: PriceAdjustmentFormSchedule
}

/**
 * What `normalizePriceAdjustmentPayload` sends to the create and update endpoints. It is built from
 * validated form values: `priceAdjustmentForm` checks the fields each start, end and scope type
 * requires (`PriceAdjustmentInput`) before the form submits.
 */
export interface PriceAdjustmentPayload {
  name: string | null
  scope_type: PriceAdjustmentScopeType | ''
  when_type: PriceAdjustmentWhenType
  collection_ids: number[]
  rounding_mode: PriceAdjustmentRoundingMode
  adjustment_type: PriceAdjustmentType | ''
  operation: PriceAdjustmentOperation | ''
  /** Rounded to cents; left as typed when it isn't a number. */
  amount: number | string
  priority: number
  schedule: {
    start_type: PriceAdjustmentStartType | ''
    starts_on: string | null
    start_holiday: string | null
    start_occasion_id: number | null
    start_offset_days: number | null
    end_type: PriceAdjustmentEndType | ''
    ends_on: string | null
    end_holiday: string | null
    end_occasion_id: number | null
    end_offset_days: number | null
    end_days_after_start: number | null
    is_yearly: boolean
  }
}

const normalizeAmount = (value: NumberishInput) => {
  const cleanedValue =
    typeof value === 'number' ? String(value) : String(value ?? '').replace(/[^0-9.-]/g, '')
  const amount = Number.parseFloat(cleanedValue)

  return Number.isFinite(amount) ? amount : Number.NaN
}

const normalizeInteger = (value: NumberishInput) => {
  const cleanedValue =
    typeof value === 'number' ? String(value) : String(value ?? '').replace(/[^0-9-]/g, '')
  const amount = Number.parseInt(cleanedValue, 10)

  return Number.isInteger(amount) ? amount : Number.NaN
}

const normalizePositiveInteger = (value: NumberishInput) => {
  const cleanedValue =
    typeof value === 'number' ? String(value) : String(value ?? '').replace(/[^0-9.-]/g, '')
  const amount = Number.parseInt(cleanedValue, 10)

  return Number.isInteger(amount) ? Math.abs(amount) : Number.NaN
}

const formatPositiveOffsetForForm = (value: NumberishInput) => {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  const amount = Number.parseInt(String(value), 10)
  return Number.isInteger(amount) ? String(Math.abs(amount)) : String(value)
}

const normalizeCollectionIds = (value: readonly (number | string)[] | null | undefined) =>
  Array.isArray(value)
    ? Array.from(
        new Set(value.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0))
      )
    : []

const normalizeEnumValue = <TValue extends string>(
  value: TValue | null | undefined
): TValue | '' => (typeof value === 'string' ? value : '')
const normalizePriority = (value: NumberishInput) => {
  const priority = Number.parseInt(String(value ?? PRICE_ADJUSTMENT_PRIORITY_NORMAL), 10)

  return Number.isInteger(priority) ? priority : Number.NaN
}
const PRICE_ADJUSTMENT_WHEN_TYPES: PriceAdjustmentWhenType[] = [
  PRICE_ADJUSTMENT_WHEN_TYPE_ACTIVE_UNTIL_DELETED,
  PRICE_ADJUSTMENT_WHEN_TYPE_SCHEDULED,
  PRICE_ADJUSTMENT_WHEN_TYPE_PERMANENT,
]
const normalizeWhenType = (value: string | null | undefined): PriceAdjustmentWhenType | '' => {
  const whenType = normalizeEnumValue(value)

  return PRICE_ADJUSTMENT_WHEN_TYPES.find((knownWhenType) => knownWhenType === whenType) ?? ''
}
const isLeapDayDate = (value: string | null | undefined) =>
  typeof value === 'string' && /^\d{4}-02-29$/.test(value)

const optionalTextField = z.union([z.literal(''), z.string()]).optional()
const optionalNumberishField = z.union([z.literal(''), z.string(), z.number(), z.null()]).optional()

export const priceAdjustmentForm = z
  .object({
    name: z.string().max(255, 'Keep the internal label under 255 characters.').default(''),
    scope_type: z.union([
      z.literal(''),
      z.enum([PRICE_ADJUSTMENT_SCOPE_GLOBAL, PRICE_ADJUSTMENT_SCOPE_COLLECTIONS]),
    ]),
    when_type: z.union([
      z.literal(''),
      z.enum([
        PRICE_ADJUSTMENT_WHEN_TYPE_ACTIVE_UNTIL_DELETED,
        PRICE_ADJUSTMENT_WHEN_TYPE_SCHEDULED,
        PRICE_ADJUSTMENT_WHEN_TYPE_PERMANENT,
      ]),
    ]),
    collection_ids: z.array(idSchema('collection')).default([]),
    rounding_mode: z.union([
      z.literal(''),
      z.enum([
        PRICE_ADJUSTMENT_ROUNDING_NONE,
        PRICE_ADJUSTMENT_ROUNDING_NEAREST_HALF_DOLLAR,
        PRICE_ADJUSTMENT_ROUNDING_NEAREST_DOLLAR,
      ]),
    ]),
    adjustment_type: z.union([z.literal(''), z.enum(['percent', 'flat'])]),
    operation: z.union([z.literal(''), z.enum(['increase', 'decrease'])]),
    amount: z.union([z.string(), z.number()]),
    priority: z.union([z.string(), z.number()]).default(PRICE_ADJUSTMENT_PRIORITY_NORMAL),
    schedule: z.object({
      is_enabled: z.boolean(),
      start_type: z.union([
        z.literal(''),
        z.enum([
          PRICE_ADJUSTMENT_START_TYPE_FIXED_DATE,
          PRICE_ADJUSTMENT_START_TYPE_HOLIDAY_OFFSET,
          PRICE_ADJUSTMENT_START_TYPE_PERMANENT,
        ]),
      ]),
      starts_on: optionalTextField,
      start_holiday: optionalTextField,
      start_occasion_id: optionalNumberishField,
      start_offset_days: optionalNumberishField,
      end_type: z.union([
        z.literal(''),
        z.enum([
          PRICE_ADJUSTMENT_END_TYPE_NONE,
          PRICE_ADJUSTMENT_END_TYPE_FIXED_DATE,
          PRICE_ADJUSTMENT_END_TYPE_HOLIDAY_OFFSET,
          PRICE_ADJUSTMENT_END_TYPE_DAYS_AFTER_START,
        ]),
      ]),
      ends_on: optionalTextField,
      end_holiday: optionalTextField,
      end_occasion_id: optionalNumberishField,
      end_offset_days: optionalNumberishField,
      end_days_after_start: optionalNumberishField,
      is_yearly: z.boolean(),
      end_matches_start_holiday: z.boolean().optional(),
    }),
  })
  .superRefine((value, ctx) => {
    if (!value.scope_type) {
      ctx.addIssue({
        code: 'custom',
        path: ['scope_type'],
        message: 'Choose where this price change should apply.',
      })
    }

    if (
      value.scope_type === PRICE_ADJUSTMENT_SCOPE_GLOBAL &&
      normalizeCollectionIds(value.collection_ids).length > 0
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['collection_ids'],
        message: 'Price changes for all products cannot also target specific collections.',
      })
    }

    if (
      value.scope_type === PRICE_ADJUSTMENT_SCOPE_COLLECTIONS &&
      normalizeCollectionIds(value.collection_ids).length === 0
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['collection_ids'],
        message: 'Choose at least one collection for this price change.',
      })
    }

    if (!value.operation) {
      ctx.addIssue({
        code: 'custom',
        path: ['operation'],
        message: 'Choose whether this price change increases or decreases prices.',
      })
    }

    if (!value.rounding_mode) {
      ctx.addIssue({
        code: 'custom',
        path: ['rounding_mode'],
        message: 'Choose how adjusted prices should be rounded.',
      })
    }

    if (!value.adjustment_type) {
      ctx.addIssue({
        code: 'custom',
        path: ['adjustment_type'],
        message: 'Choose whether this price change uses a percent or flat amount.',
      })
    }

    const amount = normalizeAmount(value.amount)
    if (!Number.isFinite(amount) || amount <= 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['amount'],
        message: 'Enter an amount greater than zero.',
      })
    }

    const startType = normalizeEnumValue(value.schedule?.start_type)
    const endType = normalizeEnumValue(value.schedule?.end_type)
    const startsOn = value.schedule?.starts_on || ''
    const endsOn = value.schedule?.ends_on || ''
    const startHoliday = value.schedule?.start_holiday || ''
    const endHoliday = value.schedule?.end_holiday || ''
    const startOffsetDays = normalizePositiveInteger(value.schedule?.start_offset_days)
    const endOffsetDays = normalizePositiveInteger(value.schedule?.end_offset_days)
    const endDaysAfterStart = normalizeInteger(value.schedule?.end_days_after_start)
    const isYearly = Boolean(value.schedule?.is_yearly)
    const priority = normalizePriority(value.priority)
    const whenType = normalizeWhenType(value.when_type)

    if (
      priority !== PRICE_ADJUSTMENT_PRIORITY_NORMAL &&
      priority !== PRICE_ADJUSTMENT_PRIORITY_HIGH
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['priority'],
        message: 'Choose Normal or Priority.',
      })
    }

    if (!whenType) {
      ctx.addIssue({
        code: 'custom',
        path: ['when_type'],
        message: 'Choose when this price change should happen.',
      })
    }

    if (!startType) {
      ctx.addIssue({
        code: 'custom',
        path: ['schedule', 'start_type'],
        message: 'Choose how this price change should start.',
      })
    }

    if (!endType) {
      ctx.addIssue({
        code: 'custom',
        path: ['schedule', 'end_type'],
        message: 'Choose how this price change should end.',
      })
    }

    if (
      whenType === PRICE_ADJUSTMENT_WHEN_TYPE_ACTIVE_UNTIL_DELETED &&
      startType !== PRICE_ADJUSTMENT_START_TYPE_FIXED_DATE
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['schedule', 'start_type'],
        message: 'Active until deleted price changes must use a fixed start date.',
      })
    }

    if (
      whenType === PRICE_ADJUSTMENT_WHEN_TYPE_ACTIVE_UNTIL_DELETED &&
      endType !== PRICE_ADJUSTMENT_END_TYPE_NONE
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['schedule', 'end_type'],
        message: 'Active until deleted price changes cannot have an automatic end date.',
      })
    }

    if (whenType === PRICE_ADJUSTMENT_WHEN_TYPE_ACTIVE_UNTIL_DELETED && isYearly) {
      ctx.addIssue({
        code: 'custom',
        path: ['schedule', 'is_yearly'],
        message: 'Active until deleted price changes cannot repeat yearly.',
      })
    }

    if (
      whenType === PRICE_ADJUSTMENT_WHEN_TYPE_PERMANENT &&
      startType !== PRICE_ADJUSTMENT_START_TYPE_PERMANENT
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['schedule', 'start_type'],
        message: 'Permanent price changes must use the permanent timing mode.',
      })
    }

    if (
      whenType === PRICE_ADJUSTMENT_WHEN_TYPE_SCHEDULED &&
      startType === PRICE_ADJUSTMENT_START_TYPE_PERMANENT
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['schedule', 'start_type'],
        message: 'Scheduled price changes must use a date or holiday start.',
      })
    }

    if (startType === PRICE_ADJUSTMENT_START_TYPE_FIXED_DATE && !startsOn) {
      ctx.addIssue({
        code: 'custom',
        path: ['schedule', 'starts_on'],
        message: 'Start date is required.',
      })
    }

    if (startType === PRICE_ADJUSTMENT_START_TYPE_HOLIDAY_OFFSET) {
      if (!startHoliday) {
        ctx.addIssue({
          code: 'custom',
          path: ['schedule', 'start_holiday'],
          message: 'Choose a holiday for the start rule.',
        })
      }

      if (!Number.isInteger(startOffsetDays) || startOffsetDays < 0 || startOffsetDays > 90) {
        ctx.addIssue({
          code: 'custom',
          path: ['schedule', 'start_offset_days'],
          message: 'Days before holiday must be between 0 and 90.',
        })
      }
    }

    if (
      startType === PRICE_ADJUSTMENT_START_TYPE_PERMANENT &&
      endType !== PRICE_ADJUSTMENT_END_TYPE_NONE
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['schedule', 'end_type'],
        message: 'Permanent price changes cannot have an end date.',
      })
    }

    if (endType === PRICE_ADJUSTMENT_END_TYPE_FIXED_DATE && !endsOn) {
      ctx.addIssue({
        code: 'custom',
        path: ['schedule', 'ends_on'],
        message: 'End date is required.',
      })
    }

    if (endType === PRICE_ADJUSTMENT_END_TYPE_HOLIDAY_OFFSET) {
      if (!endHoliday) {
        ctx.addIssue({
          code: 'custom',
          path: ['schedule', 'end_holiday'],
          message: 'Choose a holiday for the end rule.',
        })
      }

      if (!Number.isInteger(endOffsetDays) || endOffsetDays < 0 || endOffsetDays > 90) {
        ctx.addIssue({
          code: 'custom',
          path: ['schedule', 'end_offset_days'],
          message: 'Days after holiday must be between 0 and 90.',
        })
      }
    }

    if (endType === PRICE_ADJUSTMENT_END_TYPE_DAYS_AFTER_START) {
      if (
        !Number.isInteger(endDaysAfterStart) ||
        endDaysAfterStart < 0 ||
        endDaysAfterStart > 365
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['schedule', 'end_days_after_start'],
          message: 'Days after start must be between 0 and 365.',
        })
      }
    }

    if (
      startType === PRICE_ADJUSTMENT_START_TYPE_HOLIDAY_OFFSET &&
      (endType === PRICE_ADJUSTMENT_END_TYPE_FIXED_DATE ||
        endType === PRICE_ADJUSTMENT_END_TYPE_DAYS_AFTER_START)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['schedule', 'end_type'],
        message: 'Holiday-based starts only support an end type of holiday offset.',
      })
    }

    if (
      (startType === PRICE_ADJUSTMENT_START_TYPE_HOLIDAY_OFFSET || isYearly) &&
      endType === PRICE_ADJUSTMENT_END_TYPE_NONE
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['schedule', 'end_type'],
        message: 'Recurring schedules require an end rule.',
      })
    }

    if (startType !== PRICE_ADJUSTMENT_START_TYPE_FIXED_DATE && isYearly) {
      ctx.addIssue({
        code: 'custom',
        path: ['schedule', 'is_yearly'],
        message: 'Yearly recurrence is only available for fixed-date schedules.',
      })
    }

    if (
      startType === PRICE_ADJUSTMENT_START_TYPE_FIXED_DATE &&
      isYearly &&
      isLeapDayDate(startsOn)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['schedule', 'starts_on'],
        message:
          'Yearly fixed-date schedules cannot start on Feb. 29. Choose Feb. 28 or Mar. 1 instead.',
      })
    }

    if (endType === PRICE_ADJUSTMENT_END_TYPE_FIXED_DATE && isYearly && isLeapDayDate(endsOn)) {
      ctx.addIssue({
        code: 'custom',
        path: ['schedule', 'ends_on'],
        message:
          'Yearly fixed-date schedules cannot end on Feb. 29. Choose Feb. 28 or Mar. 1 instead.',
      })
    }

    if (
      startType === PRICE_ADJUSTMENT_START_TYPE_FIXED_DATE &&
      endType === PRICE_ADJUSTMENT_END_TYPE_FIXED_DATE &&
      startsOn &&
      endsOn &&
      !isYearly &&
      dayjs(endsOn).isBefore(dayjs(startsOn), 'day')
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['schedule', 'ends_on'],
        message: 'End date must be on or after the start date for non-recurring fixed schedules.',
      })
    }
  })

export const getEmptyPriceAdjustmentValues = ({
  collectionId = '',
}: {
  /** Starts a collection price change for this collection. */
  collectionId?: number | string
} = {}): PriceAdjustmentFormValues => ({
  name: '',
  scope_type: collectionId ? PRICE_ADJUSTMENT_SCOPE_COLLECTIONS : PRICE_ADJUSTMENT_SCOPE_GLOBAL,
  when_type: PRICE_ADJUSTMENT_WHEN_TYPE_ACTIVE_UNTIL_DELETED,
  collection_ids: collectionId ? [String(collectionId)] : [],
  rounding_mode: PRICE_ADJUSTMENT_ROUNDING_NEAREST_DOLLAR,
  adjustment_type: 'percent',
  operation: 'increase',
  amount: '',
  priority: PRICE_ADJUSTMENT_PRIORITY_NORMAL,
  schedule: {
    is_enabled: true,
    suppressed_until_on: null,
    start_type: PRICE_ADJUSTMENT_START_TYPE_FIXED_DATE,
    starts_on: dayjs().format('YYYY-MM-DD'),
    start_holiday: '',
    start_occasion_id: null,
    start_offset_days: '',
    end_type: PRICE_ADJUSTMENT_END_TYPE_NONE,
    ends_on: '',
    end_holiday: '',
    end_occasion_id: null,
    end_offset_days: '',
    end_days_after_start: '',
    is_yearly: false,
    end_matches_start_holiday: true,
  },
})

/** The saved form fields this legacy-aware mapper consumes. Older saved rows may omit
 * when_type and schedule fields; the existing mapping body owns those fallback choices.
 * The serialized ProductPriceAdjustment resource remains strict.
 */
type PriceAdjustmentMappingValue = Partial<
  Pick<
    ProductPriceAdjustment,
    | 'name'
    | 'scope_type'
    | 'when_type'
    | 'can_still_affect_future_pricing'
    | 'collection_ids'
    | 'rounding_mode'
    | 'adjustment_type'
    | 'operation'
    | 'amount'
    | 'priority'
  >
> & {
  collections?: Pick<ProductPriceAdjustment['collections'][number], 'id'>[]
  schedule?: Partial<ProductPriceAdjustment['schedule']>
}

export const mapPriceAdjustmentToFormValues = (
  value: PriceAdjustmentMappingValue
): PriceAdjustmentFormValues => {
  const schedule = value?.schedule || {}
  const startType =
    schedule?.start_type ||
    (schedule?.start_holiday
      ? PRICE_ADJUSTMENT_START_TYPE_HOLIDAY_OFFSET
      : PRICE_ADJUSTMENT_START_TYPE_FIXED_DATE)
  const endType =
    startType === PRICE_ADJUSTMENT_START_TYPE_HOLIDAY_OFFSET
      ? PRICE_ADJUSTMENT_END_TYPE_HOLIDAY_OFFSET
      : schedule?.end_type ||
        (schedule?.end_holiday
          ? PRICE_ADJUSTMENT_END_TYPE_HOLIDAY_OFFSET
          : schedule?.end_days_after_start !== null && schedule?.end_days_after_start !== undefined
            ? PRICE_ADJUSTMENT_END_TYPE_DAYS_AFTER_START
            : schedule?.ends_on
              ? PRICE_ADJUSTMENT_END_TYPE_FIXED_DATE
              : PRICE_ADJUSTMENT_END_TYPE_NONE)
  const whenType =
    normalizeWhenType(value?.when_type) ||
    (startType === PRICE_ADJUSTMENT_START_TYPE_PERMANENT
      ? PRICE_ADJUSTMENT_WHEN_TYPE_PERMANENT
      : startType === PRICE_ADJUSTMENT_START_TYPE_FIXED_DATE &&
          endType === PRICE_ADJUSTMENT_END_TYPE_NONE &&
          !schedule?.is_yearly
        ? PRICE_ADJUSTMENT_WHEN_TYPE_ACTIVE_UNTIL_DELETED
        : PRICE_ADJUSTMENT_WHEN_TYPE_SCHEDULED)
  const startHoliday = schedule?.start_holiday || ''
  const endHoliday =
    startType === PRICE_ADJUSTMENT_START_TYPE_HOLIDAY_OFFSET &&
    endType === PRICE_ADJUSTMENT_END_TYPE_HOLIDAY_OFFSET
      ? startHoliday
      : schedule?.end_holiday || ''
  const startOccasionId = normalizeNullableId(
    schedule?.start_occasion_id ?? schedule?.start_occasion?.id
  )

  return {
    name: value?.name || '',
    scope_type: value?.scope_type || PRICE_ADJUSTMENT_SCOPE_GLOBAL,
    when_type: whenType,
    can_still_affect_future_pricing: Boolean(value?.can_still_affect_future_pricing),
    collection_ids: (value?.collection_ids || value?.collections?.map((item) => item.id) || []).map(
      (collectionId) => String(collectionId)
    ),
    rounding_mode: value?.rounding_mode || PRICE_ADJUSTMENT_ROUNDING_NEAREST_DOLLAR,
    adjustment_type: value?.adjustment_type || 'percent',
    operation: value?.operation || 'increase',
    amount: String(value?.amount ?? ''),
    priority: normalizePriority(value?.priority) || PRICE_ADJUSTMENT_PRIORITY_NORMAL,
    schedule: {
      is_enabled: Boolean(schedule?.is_enabled),
      suppressed_until_on: schedule?.suppressed_until_on || null,
      start_type: startType,
      starts_on: schedule?.starts_on || '',
      start_holiday: startHoliday,
      start_occasion_id: startOccasionId,
      start_offset_days:
        schedule?.start_offset_days === null || schedule?.start_offset_days === undefined
          ? ''
          : formatPositiveOffsetForForm(schedule.start_offset_days),
      end_type: endType,
      ends_on: schedule?.ends_on || '',
      end_holiday: endHoliday,
      end_occasion_id:
        startType === PRICE_ADJUSTMENT_START_TYPE_HOLIDAY_OFFSET &&
        endType === PRICE_ADJUSTMENT_END_TYPE_HOLIDAY_OFFSET
          ? startOccasionId
          : normalizeNullableId(schedule?.end_occasion_id ?? schedule?.end_occasion?.id),
      end_offset_days:
        schedule?.end_offset_days === null || schedule?.end_offset_days === undefined
          ? ''
          : formatPositiveOffsetForForm(schedule.end_offset_days),
      end_days_after_start:
        schedule?.end_days_after_start === null || schedule?.end_days_after_start === undefined
          ? ''
          : String(schedule.end_days_after_start),
      is_yearly:
        startType === PRICE_ADJUSTMENT_START_TYPE_FIXED_DATE && Boolean(schedule?.is_yearly),
      end_matches_start_holiday: true,
    },
  }
}

export const normalizePriceAdjustmentPayload = (
  value: PriceAdjustmentFormValues
): PriceAdjustmentPayload => {
  const amount = normalizeAmount(value.amount)
  const collectionIds = normalizeCollectionIds(value.collection_ids)
  const whenType = normalizeWhenType(value.when_type)
  const selectedStartType = normalizeEnumValue(value.schedule?.start_type)
  const selectedEndType = normalizeEnumValue(value.schedule?.end_type)
  const startType =
    whenType === PRICE_ADJUSTMENT_WHEN_TYPE_PERMANENT
      ? PRICE_ADJUSTMENT_START_TYPE_PERMANENT
      : whenType === PRICE_ADJUSTMENT_WHEN_TYPE_ACTIVE_UNTIL_DELETED
        ? PRICE_ADJUSTMENT_START_TYPE_FIXED_DATE
        : selectedStartType
  const endType =
    whenType === PRICE_ADJUSTMENT_WHEN_TYPE_PERMANENT ||
    whenType === PRICE_ADJUSTMENT_WHEN_TYPE_ACTIVE_UNTIL_DELETED
      ? PRICE_ADJUSTMENT_END_TYPE_NONE
      : startType === PRICE_ADJUSTMENT_START_TYPE_HOLIDAY_OFFSET
        ? PRICE_ADJUSTMENT_END_TYPE_HOLIDAY_OFFSET
        : selectedEndType
  const isPermanent = whenType === PRICE_ADJUSTMENT_WHEN_TYPE_PERMANENT
  const isActiveUntilDeleted = whenType === PRICE_ADJUSTMENT_WHEN_TYPE_ACTIVE_UNTIL_DELETED
  const startOffsetDays = normalizePositiveInteger(value.schedule?.start_offset_days)
  const endOffsetDays = normalizePositiveInteger(value.schedule?.end_offset_days)
  const endDaysAfterStart = normalizeInteger(value.schedule?.end_days_after_start)
  const endMatchesStartHoliday =
    startType === PRICE_ADJUSTMENT_START_TYPE_HOLIDAY_OFFSET &&
    endType === PRICE_ADJUSTMENT_END_TYPE_HOLIDAY_OFFSET
      ? true
      : Boolean(value.schedule?.end_matches_start_holiday)
  const startHoliday =
    startType === PRICE_ADJUSTMENT_START_TYPE_HOLIDAY_OFFSET
      ? value.schedule?.start_holiday || null
      : null
  const endHoliday =
    endType === PRICE_ADJUSTMENT_END_TYPE_HOLIDAY_OFFSET
      ? endMatchesStartHoliday &&
        startType === PRICE_ADJUSTMENT_START_TYPE_HOLIDAY_OFFSET &&
        startHoliday
        ? startHoliday
        : value.schedule?.end_holiday || null
      : null
  const startOccasionId =
    startType === PRICE_ADJUSTMENT_START_TYPE_HOLIDAY_OFFSET
      ? normalizeNullableId(value.schedule?.start_occasion_id)
      : null
  const endOccasionId =
    endType === PRICE_ADJUSTMENT_END_TYPE_HOLIDAY_OFFSET
      ? endMatchesStartHoliday &&
        startType === PRICE_ADJUSTMENT_START_TYPE_HOLIDAY_OFFSET &&
        startOccasionId
        ? startOccasionId
        : normalizeNullableId(value.schedule?.end_occasion_id)
      : null

  return {
    name: typeof value.name === 'string' ? value.name.trim() || null : null,
    scope_type: value.scope_type,
    when_type: whenType || PRICE_ADJUSTMENT_WHEN_TYPE_SCHEDULED,
    collection_ids: value.scope_type === PRICE_ADJUSTMENT_SCOPE_COLLECTIONS ? collectionIds : [],
    rounding_mode: value.rounding_mode || PRICE_ADJUSTMENT_ROUNDING_NEAREST_DOLLAR,
    adjustment_type: value.adjustment_type,
    operation: value.operation,
    amount: Number.isFinite(amount) ? Number(amount.toFixed(2)) : value.amount,
    priority: isPermanent
      ? PRICE_ADJUSTMENT_PRIORITY_NORMAL
      : normalizePriority(value.priority) || PRICE_ADJUSTMENT_PRIORITY_NORMAL,
    schedule: {
      start_type: startType,
      starts_on:
        startType === PRICE_ADJUSTMENT_START_TYPE_FIXED_DATE && !isPermanent
          ? value.schedule?.starts_on || dayjs().format('YYYY-MM-DD')
          : null,
      start_holiday: startType === PRICE_ADJUSTMENT_START_TYPE_HOLIDAY_OFFSET ? startHoliday : null,
      start_occasion_id: startOccasionId,
      start_offset_days:
        startType === PRICE_ADJUSTMENT_START_TYPE_HOLIDAY_OFFSET &&
        Number.isInteger(startOffsetDays)
          ? -Math.abs(startOffsetDays)
          : null,
      end_type: isPermanent || isActiveUntilDeleted ? PRICE_ADJUSTMENT_END_TYPE_NONE : endType,
      ends_on:
        !isPermanent && !isActiveUntilDeleted && endType === PRICE_ADJUSTMENT_END_TYPE_FIXED_DATE
          ? value.schedule?.ends_on || null
          : null,
      end_holiday: isPermanent || isActiveUntilDeleted ? null : endHoliday,
      end_occasion_id: isPermanent || isActiveUntilDeleted ? null : endOccasionId,
      end_offset_days:
        !isPermanent &&
        !isActiveUntilDeleted &&
        endType === PRICE_ADJUSTMENT_END_TYPE_HOLIDAY_OFFSET &&
        Number.isInteger(endOffsetDays)
          ? Math.abs(endOffsetDays)
          : null,
      end_days_after_start:
        !isPermanent &&
        !isActiveUntilDeleted &&
        endType === PRICE_ADJUSTMENT_END_TYPE_DAYS_AFTER_START &&
        Number.isInteger(endDaysAfterStart)
          ? endDaysAfterStart
          : null,
      is_yearly:
        !isPermanent &&
        !isActiveUntilDeleted &&
        startType === PRICE_ADJUSTMENT_START_TYPE_FIXED_DATE &&
        Boolean(value.schedule?.is_yearly),
    },
  }
}
